/**
 * Tracking devices — telemetry units registered with a telematics provider.
 * Single-schema: `/api/device`. Legacy: go-metro-vehicles.
 */
import {
  DEFAULT_PAGE_SIZE,
  TELEMATICS_PROVIDER_BY_KEY,
  TELEMATICS_PROVIDERS,
  type TelematicsProviderKey,
} from '@/config/fleetConstants'
import { DeviceApiService, type ApiDevice, type ApiTelematicsProvider } from '@/services/deviceApiService'
import { GoMetroVehicleApiService } from '@/services/goMetroVehicleApiService'
import type { GoMetroVehicle, GoMetroVehicleFilters } from '@/services/goMetroVehicleApiService'
import { isSingleSchemaEnabled } from '@/services/regionScope'

export type { TelematicsProviderKey }

export interface TrackingDevice {
  id: number
  code?: string
  telemetry_id: string
  name: string
  model: string
  telematics_provider: string
  status: string
  asset_id?: number | null
  asset_number_plate?: string | null
  license_plate?: string
  vin?: string
  created_at?: string
  updated_at?: string
}

export type TrackingDeviceFilters = GoMetroVehicleFilters & {
  telematics_provider?: string
  /** Server search across serial, name, model, code. */
  search?: string
}

let providerCatalog: Map<string, string> | null = null
let enabledProviderCodes: Set<string> | null = null

export function setTelematicsProviderCatalog(providers: ApiTelematicsProvider[]): void {
  providerCatalog = new Map(providers.map((p) => [p.code, p.name]))
  enabledProviderCodes = new Set(providers.filter((p) => p.enabled).map((p) => p.code))
}

function fallbackProviderCatalog(): Map<string, string> {
  return new Map(TELEMATICS_PROVIDERS.map((p) => [p.key, p.label]))
}

/** Normalize VaSelect objects, API strings, or backend codes to a provider code. */
export function normalizeTelematicsProviderKey(value: unknown): string | null {
  return normalizeTelematicsProviderCode(value)
}

export function telematicsProviderLabel(value: unknown): string {
  const key = normalizeTelematicsProviderCode(value)
  if (!key) return 'Unknown'
  if (providerCatalog?.has(key)) return providerCatalog.get(key)!
  if (key in TELEMATICS_PROVIDER_BY_KEY) return TELEMATICS_PROVIDER_BY_KEY[key as TelematicsProviderKey].label
  return key
}

function normalizeTelematicsProviderCode(value: unknown): string | null {
  if (value == null || value === '') return null
  if (typeof value === 'object' && value !== null && 'value' in value) {
    return normalizeTelematicsProviderCode((value as { value: unknown }).value)
  }
  const key = String(value).toLowerCase().replace(/-/g, '_').trim()
  return key || null
}

function normalizeAssetId(value: unknown): number | null {
  if (value == null || value === '') return null
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'object' && value !== null && 'value' in value) {
    return normalizeAssetId((value as { value: unknown }).value)
  }
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function enrichDeviceAsset(mapped: TrackingDevice, source: Partial<TrackingDevice>): TrackingDevice {
  const requestedAssetId = normalizeAssetId(source.asset_id)
  if (requestedAssetId != null) {
    return {
      ...mapped,
      asset_id: requestedAssetId,
      asset_number_plate: mapped.asset_number_plate ?? source.asset_number_plate ?? null,
    }
  }
  if (source.asset_id === null) {
    return { ...mapped, asset_id: null, asset_number_plate: null }
  }
  return mapped
}

function mapApiDeviceToTrackingDevice(row: ApiDevice): TrackingDevice {
  const provider = normalizeTelematicsProviderCode(row.telematics_provider) ?? 'go_metro'

  return {
    id: row.id,
    code: row.code,
    telemetry_id: row.serial,
    name: row.name,
    model: row.model,
    telematics_provider: provider,
    status: row.enabled ? 'active' : 'inactive',
    asset_id: normalizeAssetId(row.vehicle?.id ?? row.vehicle_id),
    asset_number_plate: row.vehicle?.plate ?? row.vehicle?.name ?? null,
    created_at: row.created_at,
    updated_at: row.updated_at,
  }
}

function mapGoMetroToDevice(row: GoMetroVehicle): TrackingDevice {
  const provider =
    normalizeTelematicsProviderCode(row.telematics_provider) ??
    normalizeTelematicsProviderCode((row as Record<string, unknown>).telematicsProvider) ??
    'go_metro'

  return {
    id: row.id,
    telemetry_id: row.deviceId,
    name: row.name,
    model: row.model,
    telematics_provider: provider,
    status: row.status,
    asset_id: row.valt_vehicle?.id ?? null,
    asset_number_plate: row.valt_vehicle?.number_plate ?? row.licensePlate ?? null,
    license_plate: row.licensePlate,
    vin: row.vin,
    created_at: row.created_at,
    updated_at: row.updated_at,
  }
}

function mapDeviceToGoMetro(device: Partial<TrackingDevice>): Record<string, unknown> {
  const provider = normalizeTelematicsProviderKey(device.telematics_provider) ?? 'go_metro'
  return {
    deviceId: device.telemetry_id,
    name: device.name,
    model: device.model,
    licensePlate: device.license_plate ?? device.asset_number_plate ?? '',
    vin: device.vin ?? '',
    status: device.status ?? 'active',
    telematics_provider: provider,
  }
}

function toApiDevicePayload(device: Partial<TrackingDevice>) {
  return {
    ...(device.code ? { code: device.code } : {}),
    name: device.name ?? '',
    model: device.model ?? '',
    serial: device.telemetry_id ?? '',
    telematics_provider: normalizeTelematicsProviderKey(device.telematics_provider),
    enabled: device.status ? device.status !== 'inactive' : true,
    vehicle_id: normalizeAssetId(device.asset_id),
  }
}

function applyClientFilters(devices: TrackingDevice[], filters?: TrackingDeviceFilters): TrackingDevice[] {
  let result = devices
  if (filters?.deviceId) {
    const q = filters.deviceId.toLowerCase()
    result = result.filter((d) => d.telemetry_id.toLowerCase().includes(q))
  }
  if (filters?.name) {
    const q = filters.name.toLowerCase()
    result = result.filter((d) => d.name.toLowerCase().includes(q))
  }
  return result
}

export class TrackingDeviceApiService {
  static getProviderOptions() {
    const catalog = providerCatalog ?? fallbackProviderCatalog()
    return [...catalog.entries()].map(([code, name]) => ({
      value: code,
      text: `${name} (${code})`,
    }))
  }

  static getEnabledProviderOptions() {
    const catalog = providerCatalog ?? fallbackProviderCatalog()
    const codes = enabledProviderCodes ?? new Set(catalog.keys())
    return [...codes].map((code) => ({
      value: code,
      text: `${catalog.get(code) ?? code} (${code})`,
    }))
  }

  static async fetchProviders(): Promise<ApiTelematicsProvider[]> {
    if (!isSingleSchemaEnabled()) {
      const legacy = TELEMATICS_PROVIDERS.map((p) => ({
        id: 0,
        code: p.key,
        name: p.label,
        enabled: true,
      }))
      setTelematicsProviderCatalog(legacy)
      return legacy
    }

    const providers = await DeviceApiService.listProviders()
    setTelematicsProviderCatalog(providers)
    return providers
  }

  static defaultProviderCode(): string {
    const first = this.getEnabledProviderOptions()[0]?.value
    return typeof first === 'string' ? first : 'go_metro'
  }

  static async list(filters?: TrackingDeviceFilters): Promise<{
    devices: TrackingDevice[]
    pagination: { current_page: number; per_page: number; total: number; last_page: number }
  }> {
    const perPage = filters?.per_page ?? DEFAULT_PAGE_SIZE
    const page = filters?.page ?? 1
    const providerFilter = filters?.telematics_provider
      ? normalizeTelematicsProviderKey(filters.telematics_provider)
      : null
    const search = (filters?.search ?? filters?.deviceId ?? filters?.name)?.trim() || undefined

    if (isSingleSchemaEnabled()) {
      const res = await DeviceApiService.list({
        page,
        per_page: perPage,
        search,
        ...(providerFilter ? { telematics_provider: providerFilter } : {}),
      })
      return {
        devices: res.data.map((row) => mapApiDeviceToTrackingDevice(row)),
        pagination: res.pagination,
      }
    }

    const res = await GoMetroVehicleApiService.getVehicles({
      ...filters,
      page,
      per_page: perPage,
      deviceId: search ?? filters?.deviceId,
      telematics_provider: providerFilter ?? undefined,
    })

    let devices = res.data.map((v) => mapGoMetroToDevice(v))
    if (providerFilter) {
      devices = devices.filter((d) => d.telematics_provider === providerFilter)
    }
    if (search) {
      devices = applyClientFilters(devices, { deviceId: search, name: search })
    }

    return {
      devices,
      pagination: {
        ...res.pagination,
        total: providerFilter || search ? devices.length : res.pagination.total,
      },
    }
  }

  static async create(device: Omit<TrackingDevice, 'id'>): Promise<TrackingDevice> {
    if (isSingleSchemaEnabled()) {
      const created = await DeviceApiService.create(toApiDevicePayload(device))
      return enrichDeviceAsset(mapApiDeviceToTrackingDevice(created), device)
    }

    const created = await GoMetroVehicleApiService.createVehicle(mapDeviceToGoMetro(device) as never)
    return mapGoMetroToDevice(created)
  }

  static async update(id: number, device: Partial<TrackingDevice>): Promise<TrackingDevice> {
    if (isSingleSchemaEnabled()) {
      const payload = toApiDevicePayload(device)
      const updated = await DeviceApiService.update(id, payload)
      return enrichDeviceAsset(mapApiDeviceToTrackingDevice(updated), device)
    }

    const updated = await GoMetroVehicleApiService.updateVehicle(id, mapDeviceToGoMetro(device) as never)
    return mapGoMetroToDevice(updated)
  }

  static async getById(id: number): Promise<TrackingDevice> {
    if (isSingleSchemaEnabled()) {
      const row = await DeviceApiService.get(id)
      return mapApiDeviceToTrackingDevice(row)
    }

    const res = await GoMetroVehicleApiService.getVehicles({ per_page: 500 })
    const match = res.data.find((v) => v.id === id)
    if (!match) {
      throw new Error(`Device ${id} not found`)
    }
    return mapGoMetroToDevice(match)
  }

  static async remove(id: number): Promise<void> {
    if (isSingleSchemaEnabled()) {
      await DeviceApiService.remove(id)
      return
    }
    await GoMetroVehicleApiService.deleteVehicle(id)
  }
}
